import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Activity, ChevronRight, Clock3, Database, Package, Thermometer } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceArea,
  ReferenceLine,
  BarChart,
  Bar,
  Cell,
} from "recharts";

import { useAppQueryClient } from "../lib/queryClient";
import { buildTemperatureChartData, computeTemperatureDomain, seriesKey } from "../lib/chartUtils";
import { BRAND_COLORS } from "../lib/brandColors";
import {
  PerfHttpError,
  fetchJsonWithPerf,
  markFrontMilestone,
  useObserveFrontQuery,
  usePagePerfTrace,
} from "../lib/perfAudit";
import { TEMPERATURE_COLOR_PALETTE, SAFE_TEMPERATURE_RANGE } from "../lib/temperature";
import type { ConsignmentDetail, DatasetSettings, TemperatureSeries } from "../types";
import { ConsignmentRouteMap } from "../components/ConsignmentRouteMap";

const UNKNOWN_PACKAGE_TYPE = "Non renseigne";
const EVENT_LABEL_Y = 10;
const EVENT_DOT_RADIUS = 4;
const EVENT_BUBBLE_HEIGHT = 18;
const EVENT_BUBBLE_MIN_WIDTH = 26;
const EVENT_LABEL_HORIZONTAL_PADDING = 10;
const TRANSPORT_MODE_LABEL_Y_OFFSET = 23;
const CHART_HOUR_EPSILON = 0.001;

type EventLocationSegment = {
  key: string;
  label: string;
  secondaryLabel?: string;
  title: string;
  start: number;
  end: number;
};

type EventLabelViewBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function ConsignmentDetailPage() {
  const { clientId, datasetId, consignmentId } = useParams<{
    clientId: string;
    datasetId: string;
    consignmentId: string;
  }>();
  const navigate = useNavigate();
  const perfTrace = usePagePerfTrace("ConsignmentDetailPage");

  const queryClient = useAppQueryClient();
  useObserveFrontQuery(perfTrace, {
    label: "query.consignment.consignment_detail_page",
    queryKey: ["consignment", clientId, datasetId, consignmentId],
    enabled: Boolean(clientId && datasetId && consignmentId),
  });
  const { data, isLoading, error } = useQuery(
    {
      queryKey: ["consignment", clientId, datasetId, consignmentId],
      queryFn: async () => {
        if (!clientId || !datasetId || !consignmentId) throw new Error("Missing params");
        try {
          const { data: payload } = await fetchJsonWithPerf<{ consignment: ConsignmentDetail }>(
            `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(datasetId)}/consignments/${encodeURIComponent(consignmentId)}`,
            undefined,
            {
              label: "query.consignment.consignment_detail_page",
              queryKey: ["consignment", clientId, datasetId, consignmentId],
              trace: perfTrace,
            },
          );
          return payload.consignment;
        } catch (error) {
          if (error instanceof PerfHttpError) {
            throw new Error(`Consignment ${consignmentId} introuvable`);
          }
          throw error;
        }
      },
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );

  const consignment = data ?? null;
  useObserveFrontQuery(perfTrace, {
    label: "query.dataset_settings.consignment_detail_page",
    queryKey: ["dataset-settings", clientId, datasetId],
    enabled: Boolean(clientId && datasetId),
  });
  const { data: datasetSettings } = useQuery(
    {
      queryKey: ["dataset-settings", clientId, datasetId],
      enabled: Boolean(clientId && datasetId),
      queryFn: async () => {
        if (!clientId || !datasetId) return null;
        try {
          const { data: payload } = await fetchJsonWithPerf<{ dataset?: { settings?: DatasetSettings | null } }>(
            `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(datasetId)}?analysis=0`,
            undefined,
            {
              label: "query.dataset_settings.consignment_detail_page",
              queryKey: ["dataset-settings", clientId, datasetId],
              trace: perfTrace,
            },
          );
          return payload.dataset?.settings ?? null;
        } catch (error) {
          if (error instanceof PerfHttpError) {
            return null;
          }
          throw error;
        }
      },
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );

  const safeRange = useMemo(() => {
    const defaultMin = SAFE_TEMPERATURE_RANGE.min;
    const defaultMax = SAFE_TEMPERATURE_RANGE.max;
    const min = datasetSettings?.temperatureMin ?? defaultMin;
    const max = datasetSettings?.temperatureMax ?? defaultMax;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
      return { min: defaultMin, max: defaultMax };
    }
    return { min, max };
  }, [datasetSettings?.temperatureMax, datasetSettings?.temperatureMin]);
  const configuredYAxisDomain = useMemo(() => {
    const min = datasetSettings?.chartTemperatureMin;
    const max = datasetSettings?.chartTemperatureMax;
    if (typeof min !== "number" || typeof max !== "number") return null;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) return null;
    return [min, max] as const;
  }, [datasetSettings?.chartTemperatureMax, datasetSettings?.chartTemperatureMin]);

  const series = useMemo(() => buildSeriesFromConsignment(consignment), [consignment]);
  const movements = consignment?.movements ?? [];
  const events = consignment?.events ?? [];
  const packageTypeOptions = useMemo(() => buildPackageTypeOptions(series), [series]);
  const [selectedPackageTypes, setSelectedPackageTypes] = useState<string[] | null>(null);
  const [hoveredSeriesKey, setHoveredSeriesKey] = useState<string | null>(null);
  const [lockedSeriesKey, setLockedSeriesKey] = useState<string | null>(null);
  const [activeEventLocationKey, setActiveEventLocationKey] = useState<string | null>(null);
  const activePackageTypes = useMemo(() => {
    if (selectedPackageTypes === null) return packageTypeOptions;
    return selectedPackageTypes.filter((value) => packageTypeOptions.includes(value));
  }, [packageTypeOptions, selectedPackageTypes]);
  const filteredSeries = useMemo(
    () => filterSeriesByPackageType(series, activePackageTypes),
    [activePackageTypes, series],
  );
  const seriesColorByKey = useMemo(() => {
    const colorMap = new Map<string, string>();
    series.forEach((item, index) => {
      colorMap.set(seriesKey(item), buildSeriesColor(index));
    });
    return colorMap;
  }, [series]);
  const chartData = useMemo(() => buildTemperatureChartData(filteredSeries), [filteredSeries]);
  const fullChartXDomain = useMemo(() => computeChartHoursDomain(chartData), [chartData]);
  const [chartXDomain, setChartXDomain] = useState<[number, number] | null>(null);
  const [hoveredChartHour, setHoveredChartHour] = useState<number | null>(null);
  const [isChartPanning, setIsChartPanning] = useState(false);
  const chartCanvasRef = useRef<HTMLDivElement | null>(null);
  const chartPanRef = useRef<{ startX: number; domain: [number, number]; width: number } | null>(null);
  const valueDomain = useMemo(
    () => configuredYAxisDomain ?? computeTemperatureDomain(filteredSeries, safeRange),
    [configuredYAxisDomain, filteredSeries, safeRange],
  );
  const visibleChartXDomain = chartXDomain ?? fullChartXDomain;
  const chartStartMs = useMemo(() => findFirstSeriesTimestampMs(filteredSeries), [filteredSeries]);
  const eventLocationSegments = useMemo(
    () => buildEventLocationSegments(events, filteredSeries, chartStartMs),
    [chartStartMs, events, filteredSeries],
  );
  const transportSegments = useMemo(
    () => buildTransportSegments(movements, chartStartMs, fullChartXDomain, eventLocationSegments),
    [chartStartMs, eventLocationSegments, fullChartXDomain, movements],
  );
  const storageLocationSegments = useMemo(
    () => removeTransportOverlapFromLocationSegments(eventLocationSegments, transportSegments),
    [eventLocationSegments, transportSegments],
  );
  const visibleEventLocationSegments = useMemo(
    () => clipEventLocationSegments(storageLocationSegments, visibleChartXDomain),
    [storageLocationSegments, visibleChartXDomain],
  );
  const visibleTransportSegments = useMemo(
    () => clipEventLocationSegments(transportSegments, visibleChartXDomain),
    [transportSegments, visibleChartXDomain],
  );
  const eventLocationBoundaries = useMemo(
    () => filterEventLocationBoundaries(buildEventLocationBoundaries(eventLocationSegments), visibleChartXDomain),
    [eventLocationSegments, visibleChartXDomain],
  );

  useEffect(() => {
    setSelectedPackageTypes(null);
    setActiveEventLocationKey(null);
  }, [consignmentId]);

  useEffect(() => {
    setSelectedPackageTypes((previous) => {
      if (previous === null) return null;
      const retained = previous.filter((value) => packageTypeOptions.includes(value));
      return retained.length === previous.length ? previous : retained;
    });
  }, [packageTypeOptions]);

  useEffect(() => {
    setChartXDomain(null);
    setHoveredChartHour(null);
  }, [fullChartXDomain?.[0], fullChartXDomain?.[1]]);

  const activeSeriesKey = lockedSeriesKey ?? hoveredSeriesKey;

  useEffect(() => {
    if (activeSeriesKey && !filteredSeries.some((item) => seriesKey(item) === activeSeriesKey)) {
      setHoveredSeriesKey(null);
      setLockedSeriesKey(null);
    }
  }, [activeSeriesKey, filteredSeries]);

  useEffect(() => {
    if (isLoading || error || !consignment || !clientId || !datasetId || !consignmentId) return;

    markFrontMilestone(perfTrace, "first_meaningful_data", {
      clientId,
      datasetId,
      consignmentId,
      seriesCount: series.length,
      movementCount: consignment.movements.length,
      settingsLoaded: datasetSettings !== undefined,
    });
  }, [clientId, consignment, consignmentId, datasetId, datasetSettings, error, isLoading, perfTrace, series.length]);

  useEffect(() => {
    const element = chartCanvasRef.current;
    if (!element) return;

    const handleWheel = (event: WheelEvent) => {
      if (!fullChartXDomain) return;
      event.preventDefault();

      const currentDomain = chartXDomain ?? fullChartXDomain;
      const anchor = resolveZoomAnchor(hoveredChartHour, currentDomain);
      setChartXDomain(zoomChartHoursDomain(currentDomain, fullChartXDomain, anchor, event.deltaY));
    };

    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", handleWheel);
    };
  }, [chartXDomain, fullChartXDomain, hoveredChartHour]);

  useEffect(() => {
    if (!isChartPanning) return;

    const handleMouseMove = (event: MouseEvent) => {
      const pan = chartPanRef.current;
      if (!pan || !fullChartXDomain) return;

      event.preventDefault();
      const deltaX = event.clientX - pan.startX;
      setChartXDomain(panChartHoursDomain(pan.domain, fullChartXDomain, deltaX, pan.width));
    };

    const handleMouseUp = () => {
      chartPanRef.current = null;
      setIsChartPanning(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [fullChartXDomain, isChartPanning]);

  if (isLoading) {
    return (
      <div className="dataset-detail-page dd-state-wrap">
        <p className="dd-state-message">Chargement…</p>
      </div>
    );
  }

  if (error || !consignment) {
    return (
      <div className="dataset-detail-page dd-state-wrap">
        <p className="dd-state-message">Impossible de charger la consignment demandée.</p>
        <button type="button" className="dd-back-button" onClick={() => navigate(-1)}>
          ← Retour
        </button>
      </div>
    );
  }

  const { shipment, devices, histograms } = consignment;
  const clientPath = `/clients/${encodeURIComponent(clientId ?? "")}`;
  const datasetPath = `${clientPath}/datasets/${encodeURIComponent(datasetId ?? "")}`;
  const complianceLabel = shipment.compliance === "YES" ? "Compliant" : "Non compliant";
  const lastEvent = formatDateWithTime(shipment.lastEvent);
  const metricCards: Array<{
    key: string;
    label: string;
    value: string;
    subValue?: string;
    icon: typeof Package;
  }> = [
    {
      key: "awb",
      label: "AWB",
      value: shipment.awbNumber ?? "—",
      icon: Package,
    },
    {
      key: "compliance",
      label: "Conformité",
      value: complianceLabel,
      icon: Thermometer,
    },
    {
      key: "events",
      label: "Événements",
      value: String(shipment.eventCount),
      icon: Activity,
    },
    {
      key: "last-event",
      label: "Dernier événement",
      value: lastEvent.date,
      subValue: lastEvent.time,
      icon: Clock3,
    },
  ];
  const allPackageTypesSelected =
    packageTypeOptions.length > 0 && packageTypeOptions.every((type) => activePackageTypes.includes(type));
  const includePackageTypeInLabel =
    activePackageTypes.length > 1 ||
    (activePackageTypes.length === 1 && activePackageTypes[0] === UNKNOWN_PACKAGE_TYPE);

  const handleTogglePackageType = (type: string) => {
    setSelectedPackageTypes((previous) => {
      const current = previous === null ? packageTypeOptions : previous;
      if (current.includes(type)) {
        return current.filter((value) => value !== type);
      }
      return [...current, type];
    });
  };

  const handleSelectAllPackageTypes = () => {
    setSelectedPackageTypes(packageTypeOptions);
  };

  const handleClearPackageTypes = () => {
    setSelectedPackageTypes([]);
  };

  const handleChartMouseMove = (state: { activeLabel?: unknown } | null) => {
    const hour = Number(state?.activeLabel);
    if (!Number.isFinite(hour)) return;
    setHoveredChartHour(hour);
  };

  const handleChartMouseLeave = () => {
    setHoveredChartHour(null);
  };

  const handleChartResetZoom = () => {
    setChartXDomain(null);
  };

  const handleChartMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    if (!fullChartXDomain) return;

    const element = chartCanvasRef.current;
    if (!element) return;

    event.preventDefault();
    chartPanRef.current = {
      startX: event.clientX,
      domain: chartXDomain ?? fullChartXDomain,
      width: element.clientWidth,
    };
    setIsChartPanning(true);
  };

  return (
    <div className="dataset-detail-page dd-page-wrapper consignment-detail-page">
      <nav className="dd-top-nav">
        <div className="dd-top-nav-inner">
          <div className="dd-logo-section">
            <div className="dd-logo-badge" aria-hidden="true">
              <Database />
            </div>
            <Link to="/" className="dd-logo-link">
              <span>Lainvision</span>
              <span className="dd-particle-logo" aria-hidden="true"></span>
            </Link>
            <div className="dd-breadcrumb">
              <Link to="/clients" className="dd-breadcrumb-link">
                Clients
              </Link>
              <ChevronRight className="dd-breadcrumb-chevron" aria-hidden="true" />
              <Link to={clientPath} className="dd-breadcrumb-link">
                {clientId}
              </Link>
              <ChevronRight className="dd-breadcrumb-chevron" aria-hidden="true" />
              <Link to={datasetPath} className="dd-breadcrumb-link">
                {datasetId}
              </Link>
              <ChevronRight className="dd-breadcrumb-chevron" aria-hidden="true" />
              <span className="dd-breadcrumb-current">{shipment.id}</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="dd-dataset-selector-card">
        <div className="dd-selector-header">
          <div className="dd-selector-left">
            <Link to={datasetPath} className="dd-back-button" aria-label="Retour au dataset">
              <span aria-hidden="true">←</span>
              <span>Retour dataset</span>
            </Link>
            <h2 className="dd-selector-title">Consignment {shipment.id}</h2>
            <p className="dd-selector-subtitle">
              <span>Client:</span>
              <Link to={clientPath} className="dd-selector-client-pill">
                {clientId}
              </Link>
              <span>Dataset:</span>
              <Link to={datasetPath} className="dd-selector-client-pill">
                {datasetId}
              </Link>
            </p>
          </div>
        </div>

        <div className="dd-countries-panel cdp-route-summary">
          <div className="cdp-route-item">
            <p className="dd-countries-title">Origine</p>
            <strong>
              {shipment.originCity ?? "—"} ({shipment.originCountry ?? "—"})
            </strong>
          </div>
          <div className="cdp-route-item">
            <p className="dd-countries-title">Destination</p>
            <strong>
              {shipment.destinationCity ?? "—"} ({shipment.destinationCountry ?? "—"})
            </strong>
          </div>
          <div className="cdp-route-item">
            <p className="dd-countries-title">Statut</p>
            <strong className={`status-chip`} data-status={shipment.compliance.toLowerCase()}>
              {complianceLabel}
            </strong>
          </div>
        </div>
      </div>

      <main className="dd-main-content">
        <div className="dd-metrics-grid">
          {metricCards.map((metric) => {
            const Icon = metric.icon;
            return (
              <article key={metric.key} className={`dd-metric-card dd-metric-card--${metric.key === "events" ? "events" : "shipments"}`}>
                <div className="dd-metric-header">
                  <div className="dd-metric-icon-wrap" aria-hidden="true">
                    <Icon className="dd-metric-icon" />
                  </div>
                  <div className="dd-metric-label">{metric.label}</div>
                </div>
                <div className="dd-metric-value-row">
                  <div className={`dd-metric-value${metric.subValue ? " dd-metric-value--small" : ""}`}>
                    {metric.value}
                  </div>
                  {metric.subValue ? <span className="dd-metric-subvalue">{metric.subValue}</span> : null}
                </div>
              </article>
            );
          })}
        </div>

        <div className="dd-tab-panel cdp-sections">
          <section className="chart-panel">
            <h3>Température détaillée</h3>
            {series.length === 0 ? (
              <p>Aucune mesure disponible.</p>
            ) : (
              <>
                <div className="cdp-temperature-controls">
                  <div className="cdp-temperature-actions">
                    <button type="button" onClick={handleSelectAllPackageTypes} disabled={allPackageTypesSelected}>
                      Tout afficher
                    </button>
                    <button type="button" onClick={handleClearPackageTypes} disabled={activePackageTypes.length === 0}>
                      Tout masquer
                    </button>
                  </div>
                  {packageTypeOptions.length > 0 ? (
                    <div className="cdp-package-chips">
                      {packageTypeOptions.map((type) => {
                        const selected = activePackageTypes.includes(type);
                        return (
                          <button
                            key={type}
                            type="button"
                            className={`cdp-package-chip${selected ? " is-active" : ""}`}
                            onClick={() => handleTogglePackageType(type)}
                          >
                            {type}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                {filteredSeries.length === 0 ? (
                  <p>Aucune mesure pour les types de package sélectionnés.</p>
                ) : (
                  <>
                    <p className="cdp-chart-hint">Survole une courbe pour afficher son label et sa valeur.</p>
                    <div className="cdp-chart-with-legend">
                      <div
                        ref={chartCanvasRef}
                        className={`cdp-chart-canvas${isChartPanning ? " is-panning" : ""}`}
                        onMouseDown={handleChartMouseDown}
                        onMouseLeave={handleChartMouseLeave}
                        onDoubleClick={handleChartResetZoom}
                      >
                        <ResponsiveContainer width="100%" height={500}>
                          <LineChart
                            data={chartData}
                            margin={{ top: 58, right: 40, left: 0, bottom: 14 }}
                            onMouseMove={handleChartMouseMove}
                          >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis
                            type="number"
                            dataKey="hours"
                            stroke="#475569"
                            allowDecimals={false}
                            allowDataOverflow
                            domain={visibleChartXDomain ?? ["dataMin", "dataMax"]}
                            tickFormatter={formatAxisTick}
                            label={{ value: "Temps (h)", position: "insideBottomRight", offset: -5, fill: "#475569" }}
                          />
                          <YAxis
                            stroke="#475569"
                            domain={[valueDomain[0], valueDomain[1]]}
                            allowDecimals={false}
                            tickFormatter={formatAxisTick}
                            label={{ value: "Température (°C)", angle: -90, position: "insideLeft", fill: "#475569" }}
                          />
                          <Tooltip
                            formatter={(value: number, key: string) => {
                              const meta = filteredSeries.find((item) => seriesKey(item) === key);
                              return [
                                `${value.toFixed(2)} °C`,
                                meta ? formatSeriesLabel(meta, { includePackageTypes: includePackageTypeInLabel }) : key,
                              ];
                            }}
                            labelFormatter={(_, payload) => {
                              const ts = payload?.[0]?.payload?.timestamp as string | undefined;
                              if (ts) return new Date(ts).toLocaleString("fr-FR");
                              return "";
                            }}
                          />
                          {visibleEventLocationSegments.map((segment, index) =>
                            segment.end > segment.start ? (
                              <ReferenceArea
                                key={`event-location-area-${segment.key}`}
                                x1={segment.start}
                                x2={segment.end}
                                fill={index % 2 === 0 ? "rgba(99, 102, 241, 0.08)" : "rgba(59, 130, 246, 0.08)"}
                                stroke="none"
                                label={{
                                  content: (props) =>
                                    renderEventLocationAreaLabel(
                                      props,
                                      segment,
                                      activeEventLocationKey,
                                      setActiveEventLocationKey,
                                    ),
                                }}
                              />
                            ) : null,
                          )}
                          <ReferenceArea
                            y1={safeRange.min}
                            y2={safeRange.max}
                            fill="#bbf7d0"
                            fillOpacity={0.25}
                            stroke="none"
                          />
                          {eventLocationBoundaries.map((boundary) => (
                            <ReferenceLine
                              key={boundary.key}
                              x={boundary.position}
                              stroke="#0f172a"
                              strokeWidth={1}
                              strokeOpacity={0.65}
                            />
                          ))}
                          {visibleTransportSegments.map((segment) => (
                            <ReferenceArea
                              key={`transport-area-${segment.key}`}
                              x1={segment.start}
                              x2={segment.end}
                              fill="rgba(240, 126, 38, 0.1)"
                              stroke="none"
                              label={{
                                content: (props) =>
                                  renderTransportSegmentLabel(
                                    props,
                                    segment,
                                    activeEventLocationKey,
                                    setActiveEventLocationKey,
                                  ),
                              }}
                            />
                          ))}
                          {visibleTransportSegments.map((segment) => (
                            <ReferenceLine
                              key={`transport-start-${segment.key}`}
                              x={segment.start}
                              stroke="#f07e26"
                              strokeWidth={1.4}
                              strokeOpacity={0.8}
                            />
                          ))}
                          {filteredSeries.map((serie) => {
                            const key = seriesKey(serie);
                            const color = seriesColorByKey.get(key) ?? TEMPERATURE_COLOR_PALETTE[0];
                            const isActive = activeSeriesKey === key;
                            const hasActive = Boolean(activeSeriesKey);
                            return (
                              <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={color}
                                strokeWidth={isActive ? 3.5 : 2.5}
                                strokeOpacity={hasActive ? (isActive ? 1 : 0.18) : 1}
                                dot={false}
                                activeDot={{ r: 4 }}
                                isAnimationActive={false}
                                connectNulls
                                name={formatSeriesLabel(serie, { includePackageTypes: includePackageTypeInLabel })}
                                onMouseEnter={() => {
                                  if (lockedSeriesKey) return;
                                  setHoveredSeriesKey(key);
                                }}
                                onMouseLeave={() => {
                                  if (lockedSeriesKey) return;
                                  setHoveredSeriesKey(null);
                                }}
                              />
                            );
                          })}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      <aside className="cdp-legend-panel" aria-label="Légende des courbes">
                        <div className="cdp-legend-header">
                          <span>Légende ({filteredSeries.length})</span>
                          <button
                            type="button"
                            className="cdp-legend-unlock"
                            onClick={() => setLockedSeriesKey(null)}
                            disabled={!lockedSeriesKey}
                          >
                            Déverrouiller
                          </button>
                        </div>
                        <div className="cdp-legend-overlay">
                          {filteredSeries.map((serie) => {
                            const key = seriesKey(serie);
                            const color = seriesColorByKey.get(key) ?? TEMPERATURE_COLOR_PALETTE[0];
                            const isActive = activeSeriesKey === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                className={`cdp-legend-item${isActive ? " is-active" : ""}`}
                                onMouseEnter={() => {
                                  if (lockedSeriesKey) return;
                                  setHoveredSeriesKey(key);
                                }}
                                onMouseLeave={() => {
                                  if (lockedSeriesKey) return;
                                  setHoveredSeriesKey(null);
                                }}
                                onClick={() => {
                                  setLockedSeriesKey((previous) => (previous === key ? null : key));
                                }}
                              >
                                <span className="cdp-legend-dot" style={{ backgroundColor: color }} />
                                <span className="cdp-legend-label">
                                  {formatSeriesLabel(serie, { includePackageTypes: includePackageTypeInLabel })}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </aside>
                    </div>
                  </>
                )}
              </>
            )}
          </section>

          <section className="chart-panel cdp-histograms-grid">
            <div>
              <h3>Breakdown of inside temperature</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={histograms.inside}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="range" stroke="#475569" allowDecimals={false} />
                  <YAxis stroke="#475569" allowDecimals={false} />
                  <Tooltip />
                  {renderHistogramSafeBand(histograms.inside, safeRange)}
                  <Bar dataKey="count" fill={BRAND_COLORS.blueDark} radius={[0, 0, 0, 0]}>
                    {histograms.inside.map((bin) => (
                      <Cell
                        key={`inside-${bin.range}`}
                        fill={isHistogramBinWithinSafeRange(bin.range, safeRange) ? BRAND_COLORS.blueLight : BRAND_COLORS.blueDark}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h3>Breakdown of outside temperature</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={histograms.outside}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="range" stroke="#475569" allowDecimals={false} />
                  <YAxis stroke="#475569" allowDecimals={false} />
                  <Tooltip />
                  {renderHistogramSafeBand(histograms.outside, safeRange)}
                  <Bar dataKey="count" fill={BRAND_COLORS.orange} radius={[0, 0, 0, 0]}>
                    {histograms.outside.map((bin) => (
                      <Cell
                        key={`outside-${bin.range}`}
                        fill={isHistogramBinWithinSafeRange(bin.range, safeRange) ? BRAND_COLORS.blueLight : BRAND_COLORS.orange}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="chart-panel">
            <h3>Sensors</h3>
            <div className="panel-table-wrapper">
              <table className="panel-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Package</th>
                    <th>Position</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Duration (h)</th>
                    <th>Excursions (h)</th>
                    <th>First excursion (h)</th>
                    <th>Min</th>
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={`${device.deviceId}-${device.position}`}>
                      <td>{formatDeviceIdentifier(shipment.id, device.deviceId)}</td>
                      <td>{formatPackageReference(device.boxIdentifier)}</td>
                      <td>{device.position ?? "—"}</td>
                      <td>{device.timeBegin ? new Date(device.timeBegin).toLocaleString("fr-FR") : "—"}</td>
                      <td>{device.timeEnd ? new Date(device.timeEnd).toLocaleString("fr-FR") : "—"}</td>
                      <td>{formatNumber(device.totalHours)}</td>
                      <td>{formatNumber(device.totalExcursionHours)}</td>
                      <td>{formatNumber(device.firstExcursionHours)}</td>
                      <td>{formatNumber(device.minValue)}</td>
                      <td>{formatNumber(device.maxValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="chart-panel">
            <h3>Événements</h3>
            {events.length === 0 ? (
              <p>Aucun événement.</p>
            ) : (
              <div className="panel-table-wrapper">
                <table className="panel-table">
                  <thead>
                    <tr>
                      <th>Heure</th>
                      <th>Code</th>
                      <th>IATA</th>
                      <th>Pays</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event, index) => (
                      <tr key={`${event.timestamp ?? index}`}>
                        <td>{event.timestamp ? new Date(event.timestamp).toLocaleString("fr-FR") : "—"}</td>
                        <td>{event.typeCode ?? "—"}</td>
                        <td>{event.iataCode ?? "—"}</td>
                        <td>{event.country ?? "—"}</td>
                        <td>{event.description ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="chart-panel">
            <h3>Steps</h3>
            {movements.length === 0 ? (
              <p>Aucun step enregistré.</p>
            ) : (
              <div className="panel-table-wrapper">
                <table className="panel-table">
                  <thead>
                    <tr>
                      <th>Step</th>
                      <th>Début (UTC)</th>
                      <th>Durée (h)</th>
                      <th>Temps relatif (h)</th>
                      <th>Rec Count</th>
                      <th>Excursion</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((movement, index) => (
                      <tr key={buildMovementRowKey(movement, index)}>
                        <td>{movement.step ?? "—"}</td>
                        <td>{movement.beginAtUTC ? new Date(movement.beginAtUTC).toLocaleString("fr-FR") : "—"}</td>
                        <td>{formatNumber(movement.duration)}</td>
                        <td>{formatNumber(movement.relTime)}</td>
                        <td>{movement.recCount ?? "—"}</td>
                        <td>{movement.excursion ?? "—"}</td>
                        <td>{movement.description ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="chart-panel">
            <h3>Trajectoire géographique</h3>
            <ConsignmentRouteMap events={events} />
          </section>
        </div>
      </main>
    </div>
  );
}

function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toFixed(2);
}

function buildMovementRowKey(movement: ConsignmentDetail["movements"][number], index: number): string {
  return [
    movement.step ?? "",
    movement.beginAtUTC ?? "",
    movement.duration ?? "",
    movement.relTime ?? "",
    movement.recCount ?? "",
    movement.description ?? "",
    index,
  ].join("|");
}

function formatDateWithTime(
  value: string | null | undefined,
): {
  date: string;
  time?: string;
} {
  if (!value) return { date: "—" };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: value };
  return {
    date: parsed.toLocaleDateString("fr-FR"),
    time: parsed.toLocaleTimeString("fr-FR"),
  };
}

function formatDeviceIdentifier(shipmentId: string, rawDeviceId: string | null | undefined): string {
  const normalized = (rawDeviceId ?? "").trim();
  if (!normalized) return "—";
  const prefix = `${shipmentId}-`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length) || normalized;
  }
  return normalized;
}

function splitBoxIdentifiers(value: string | null | undefined): string[] {
  if (!value) return [];
  const identifiers = value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return Array.from(new Set(identifiers));
}

function formatPackageReference(value: string | null | undefined): string {
  const identifiers = splitBoxIdentifiers(value);
  if (identifiers.length === 0) return "—";
  return identifiers.join(", ");
}

function toPackageType(boxIdentifier: string): string {
  const parts = boxIdentifier
    .split("-")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return (parts[parts.length - 1] ?? boxIdentifier).toUpperCase();
}

function getSeriesPackageTypes(item: TemperatureSeries): string[] {
  const identifiers = splitBoxIdentifiers(item.boxIdentifier);
  if (identifiers.length === 0) return [UNKNOWN_PACKAGE_TYPE];
  return Array.from(new Set(identifiers.map((value) => toPackageType(value))));
}

function buildPackageTypeOptions(series: TemperatureSeries[]): string[] {
  const values = new Set<string>();
  for (const item of series) {
    getSeriesPackageTypes(item).forEach((value) => values.add(value));
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

function filterSeriesByPackageType(series: TemperatureSeries[], selectedTypes: string[]): TemperatureSeries[] {
  if (selectedTypes.length === 0) return [];
  const selected = new Set(selectedTypes);
  return series.filter((item) => getSeriesPackageTypes(item).some((value) => selected.has(value)));
}

function buildSeriesFromConsignment(consignment: ConsignmentDetail | null): TemperatureSeries[] {
  if (!consignment) return [];

  const compact = consignment.sensorMeasurements;
  if (!compact) return consignment.analysis.series ?? [];

  const series = compact.sensors
    .map((sensor) => {
      const deviceId = sensor.id_sensor?.trim();
      if (!deviceId) return null;

      const device = consignment.devices.find((item) => item.deviceId === deviceId);
      return {
        shipmentId: compact.consignmentId,
        deviceId,
        label: `${compact.consignmentId} – ${deviceId}`,
        position: sensor.position,
        boxIdentifier: device?.boxIdentifier ?? null,
        destinationCity: consignment.shipment.destinationCity,
        destinationCountry: consignment.shipment.destinationCountry,
        points: buildTemperaturePoints(sensor.measurements),
      } satisfies TemperatureSeries;
    })
    .filter((item): item is TemperatureSeries => item !== null);

  return series.sort(compareTemperatureSeries);
}

function buildTemperaturePoints(
  measurements: NonNullable<ConsignmentDetail["sensorMeasurements"]>["sensors"][number]["measurements"],
) {
  const points = measurements
    .map((measurement) => ({
      timestamp: measurement.date,
      hours: 0,
      value: measurement.value,
    }))
    .filter((point) => Number.isFinite(point.value))
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  if (points.length === 0) return [];

  const firstTimestamp = new Date(points[0].timestamp).valueOf();
  if (!Number.isFinite(firstTimestamp)) return points;

  return points.map((point) => ({
    ...point,
    hours: (new Date(point.timestamp).valueOf() - firstTimestamp) / 3_600_000,
  }));
}

function compareTemperatureSeries(a: TemperatureSeries, b: TemperatureSeries): number {
  const shipmentDiff = a.shipmentId.localeCompare(b.shipmentId);
  if (shipmentDiff !== 0) return shipmentDiff;

  const positionDiff = temperaturePositionRank(a.position) - temperaturePositionRank(b.position);
  if (positionDiff !== 0) return positionDiff;

  return a.deviceId.localeCompare(b.deviceId);
}

function temperaturePositionRank(position: string | null): number {
  const normalized = (position ?? "").toUpperCase();
  if (normalized === "OUTSIDE") return 0;
  if (normalized === "INSIDE") return 1;
  return 2;
}

function buildEventLocationSegments(
  events: ConsignmentDetail["events"],
  series: TemperatureSeries[],
  chartStartMs: number | null,
) {
  const chartEndHour = findLastSeriesHour(series);
  if (chartStartMs === null || chartEndHour <= 0) {
    return [];
  }

  const markers = events
    .map((event) => {
      const label = formatEventLocationLabel(event);
      const timestampMs = parseTimestampMs(event.timestamp);
      if (!label || timestampMs === null) return null;
      const hour = (timestampMs - chartStartMs) / 3_600_000;
      if (!Number.isFinite(hour) || hour < 0 || hour > chartEndHour) return null;
      return {
        hour,
        label,
        title: formatEventLocationTitle(event, label),
      };
    })
    .filter((event): event is { hour: number; label: string; title: string } => Boolean(event))
    .sort((a, b) => a.hour - b.hour);

  const locationChanges: typeof markers = [];
  for (const marker of markers) {
    const previous = locationChanges.at(-1);
    if (previous && previous.label === marker.label) {
      continue;
    }
    locationChanges.push(marker);
  }

  return locationChanges.map((marker, index) => {
    const next = locationChanges[index + 1];
    const intervalEnd = next ? next.hour : chartEndHour;
    const start = Math.min(Math.max(marker.hour, 0), chartEndHour);
    const end = Math.min(Math.max(intervalEnd, start), chartEndHour);
    return {
      key: `${marker.label}-${index}-${marker.hour.toFixed(3)}`,
      label: marker.label,
      title: marker.title,
      start,
      end,
    };
  }).filter((segment) => segment.end > segment.start);
}

function buildTransportSegments(
  movements: ConsignmentDetail["movements"],
  chartStartMs: number | null,
  chartDomain: [number, number] | null,
  locationSegments: EventLocationSegment[],
): EventLocationSegment[] {
  if (chartStartMs === null || !chartDomain) return [];

  return movements
    .map((movement, index) => {
      const modeLabel = formatTransportModeLabel(movement);
      if (!modeLabel) return null;

      const startMs = parseTimestampMs(movement.beginAtUTC);
      if (startMs === null) return null;
      if (movement.duration === null || movement.duration <= 0) return null;

      const start = (startMs - chartStartMs) / 3_600_000;
      const end = start + movement.duration;
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

      const clippedStart = Math.max(start, chartDomain[0]);
      const clippedEnd = Math.min(end, chartDomain[1]);
      if (clippedEnd <= clippedStart) return null;

      const label = resolveTransportRouteLabel(locationSegments, start, end);
      return {
        key: `transport-${index}-${start.toFixed(3)}-${end.toFixed(3)}`,
        label,
        secondaryLabel: modeLabel,
        title: formatTransportTitle(movement, label, modeLabel),
        start: clippedStart,
        end: clippedEnd,
      };
    })
    .filter((segment): segment is EventLocationSegment => segment !== null);
}

function formatTransportModeLabel(movement: ConsignmentDetail["movements"][number]): string | null {
  const step = movement.step?.trim();
  if (!step) return null;
  const normalized = step.toUpperCase();
  if (normalized === "AIR") return "Air";
  if (normalized === "ROAD") return "Road";
  return null;
}

function resolveTransportRouteLabel(
  locationSegments: EventLocationSegment[],
  start: number,
  end: number,
): string {
  const from = findLocationLabelAtHour(locationSegments, start, false);
  const to = findLocationLabelAtHour(locationSegments, end, true);
  if (from && to && from !== to) return `${from} - ${to}`;
  if (from) return from;
  if (to) return to;
  return "Trajet";
}

function findLocationLabelAtHour(
  segments: EventLocationSegment[],
  hour: number,
  preferNextAtBoundary: boolean,
): string | null {
  if (preferNextAtBoundary) {
    const nextAtBoundary = segments.find((segment) => Math.abs(hour - segment.start) <= CHART_HOUR_EPSILON);
    if (nextAtBoundary) return nextAtBoundary.label;
  }

  const matching = segments.find(
    (segment) => hour >= segment.start - CHART_HOUR_EPSILON && hour <= segment.end + CHART_HOUR_EPSILON,
  );
  if (matching) return matching.label;

  const next = segments.find((segment) => segment.start > hour);
  if (next) return next.label;

  const previous = segments.at(-1);
  return previous?.label ?? null;
}

function formatTransportTitle(
  movement: ConsignmentDetail["movements"][number],
  routeLabel: string,
  modeLabel: string,
): string {
  const parts = [routeLabel, modeLabel];
  const description = movement.description?.trim();
  if (description) {
    parts.push(description);
  }
  if (movement.beginAtUTC) {
    parts.push(new Date(movement.beginAtUTC).toLocaleString("fr-FR"));
  }
  if (movement.duration !== null) {
    parts.push(`${movement.duration.toFixed(2)} h`);
  }
  return parts.join(" - ");
}

function removeTransportOverlapFromLocationSegments(
  locations: EventLocationSegment[],
  transports: EventLocationSegment[],
): EventLocationSegment[] {
  if (transports.length === 0) return locations;

  const output: EventLocationSegment[] = [];
  for (const location of locations) {
    let remaining = [{ start: location.start, end: location.end }];

    for (const transport of transports) {
      remaining = remaining.flatMap((part) => subtractSegmentInterval(part, transport));
      if (remaining.length === 0) break;
    }

    remaining.forEach((part, index) => {
      if (part.end <= part.start) return;
      output.push({
        ...location,
        key: `${location.key}-storage-${index}-${part.start.toFixed(3)}-${part.end.toFixed(3)}`,
        start: part.start,
        end: part.end,
      });
    });
  }

  return output;
}

function subtractSegmentInterval(
  segment: { start: number; end: number },
  interval: { start: number; end: number },
): Array<{ start: number; end: number }> {
  if (interval.end <= segment.start || interval.start >= segment.end) {
    return [segment];
  }

  const parts: Array<{ start: number; end: number }> = [];
  if (interval.start > segment.start) {
    parts.push({ start: segment.start, end: Math.min(interval.start, segment.end) });
  }
  if (interval.end < segment.end) {
    parts.push({ start: Math.max(interval.end, segment.start), end: segment.end });
  }
  return parts;
}

function clipEventLocationSegments(
  segments: EventLocationSegment[],
  visibleDomain: [number, number] | null,
): EventLocationSegment[] {
  if (!visibleDomain) return segments;

  const visibleStart = visibleDomain[0];
  const visibleEnd = visibleDomain[1];
  return segments
    .map((segment) => {
      const start = Math.max(segment.start, visibleStart);
      const end = Math.min(segment.end, visibleEnd);
      if (end <= start) return null;
      return {
        ...segment,
        key: `${segment.key}-${start.toFixed(3)}-${end.toFixed(3)}`,
        start,
        end,
      };
    })
    .filter((segment): segment is EventLocationSegment => segment !== null);
}

function renderEventLocationAreaLabel(
  props: { viewBox?: Partial<EventLabelViewBox> },
  segment: EventLocationSegment,
  activeEventLocationKey: string | null,
  setActiveEventLocationKey: (key: string | null) => void,
) {
  const box = readEventLabelViewBox(props);
  if (!box) return null;

  const label = formatEventLocationAreaLabel(segment.label, box.width);
  if (!label) {
    return renderEventLocationDot(box, segment, activeEventLocationKey, setActiveEventLocationKey);
  }

  return renderEventLocationBubble(box, segment, label);
}

function renderTransportSegmentLabel(
  props: { viewBox?: Partial<EventLabelViewBox> },
  segment: EventLocationSegment,
  activeEventLocationKey: string | null,
  setActiveEventLocationKey: (key: string | null) => void,
) {
  const box = readEventLabelViewBox(props);
  if (!box) return null;

  const width = Math.max(box.width, EVENT_BUBBLE_MIN_WIDTH);
  const height = EVENT_BUBBLE_HEIGHT;
  const x = box.x;
  const routeLabel = formatEventLocationAreaLabel(segment.label, box.width);
  const modeLabel = segment.secondaryLabel ? formatEventLocationAreaLabel(segment.secondaryLabel, box.width) : null;
  if (!routeLabel && !modeLabel) {
    return renderTransportDot(box, segment, activeEventLocationKey, setActiveEventLocationKey);
  }

  return (
    <g className="cdp-transport-label">
      <title>{segment.title}</title>
      {routeLabel ? (
        <>
          <rect x={x} y={box.y + 2} width={width} height={height} rx={9} ry={9} />
          <text x={x + width / 2} y={box.y + 15} textAnchor="middle">
            {routeLabel}
          </text>
        </>
      ) : null}
      {modeLabel ? (
        <>
          <rect x={x} y={box.y + TRANSPORT_MODE_LABEL_Y_OFFSET} width={width} height={height} rx={9} ry={9} />
          <text x={x + width / 2} y={box.y + TRANSPORT_MODE_LABEL_Y_OFFSET + 13} textAnchor="middle">
            {modeLabel}
          </text>
        </>
      ) : null}
    </g>
  );
}

function renderTransportDot(
  box: EventLabelViewBox,
  segment: EventLocationSegment,
  activeEventLocationKey: string | null,
  setActiveEventLocationKey: (key: string | null) => void,
) {
  const x = box.x + box.width / 2;
  const y = box.y + EVENT_LABEL_Y;
  const isActive = activeEventLocationKey === segment.key;

  function toggleTooltip() {
    if (isActive) {
      setActiveEventLocationKey(null);
      return;
    }
    setActiveEventLocationKey(segment.key);
  }

  return (
    <g
      className="cdp-transport-dot"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        toggleTooltip();
      }}
    >
      <title>{segment.title}</title>
      <circle cx={x} cy={y} r={EVENT_DOT_RADIUS} />
      {isActive ? renderEventLocationTooltip(x, y, segment) : null}
    </g>
  );
}

function readEventLabelViewBox(props: { viewBox?: Partial<EventLabelViewBox> }): EventLabelViewBox | null {
  const box = props.viewBox;
  if (!box) return null;
  if (typeof box.x !== "number") return null;
  if (typeof box.y !== "number") return null;
  if (typeof box.width !== "number") return null;
  if (typeof box.height !== "number") return null;
  return {
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
  };
}

function renderEventLocationBubble(box: EventLabelViewBox, segment: EventLocationSegment, label: string) {
  const width = Math.max(box.width, EVENT_BUBBLE_MIN_WIDTH);
  const height = EVENT_BUBBLE_HEIGHT;
  const x = box.x;
  const y = box.y + 2;

  return (
    <g className="cdp-event-location-label">
      <title>{segment.title}</title>
      <rect x={x} y={y} width={width} height={height} rx={9} ry={9} />
      <text x={x + width / 2} y={y + 13} textAnchor="middle">
        {label}
      </text>
    </g>
  );
}

function renderEventLocationDot(
  box: EventLabelViewBox,
  segment: EventLocationSegment,
  activeEventLocationKey: string | null,
  setActiveEventLocationKey: (key: string | null) => void,
) {
  const x = box.x + box.width / 2;
  const y = box.y + EVENT_LABEL_Y;
  const isActive = activeEventLocationKey === segment.key;

  function toggleTooltip() {
    if (isActive) {
      setActiveEventLocationKey(null);
      return;
    }
    setActiveEventLocationKey(segment.key);
  }

  return (
    <g
      className="cdp-event-location-dot"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        toggleTooltip();
      }}
    >
      <title>{segment.title}</title>
      <circle cx={x} cy={y} r={EVENT_DOT_RADIUS} />
      {isActive ? renderEventLocationTooltip(x, y, segment) : null}
    </g>
  );
}

function renderEventLocationTooltip(x: number, y: number, segment: EventLocationSegment) {
  const width = Math.min(Math.max(segment.label.length * 7 + 16, 42), 120);
  const height = 24;
  const tooltipX = x - width / 2;
  const tooltipY = y - height - 8;

  return (
    <g className="cdp-event-location-tip">
      <rect x={tooltipX} y={tooltipY} width={width} height={height} rx={6} ry={6} />
      <text x={tooltipX + 8} y={tooltipY + 16}>
        {segment.label}
      </text>
    </g>
  );
}

function formatEventLocationAreaLabel(label: string, width: number): string | null {
  const availableWidth = width - EVENT_LABEL_HORIZONTAL_PADDING * 2;
  if (availableWidth < EVENT_BUBBLE_MIN_WIDTH) {
    return null;
  }

  if (labelFitsEventBubble(label, availableWidth)) {
    return label;
  }

  const shortLabel = label.slice(0, 3);
  if (labelFitsEventBubble(shortLabel, availableWidth)) {
    return shortLabel;
  }

  return null;
}

function labelFitsEventBubble(label: string, availableWidth: number): boolean {
  return label.length * 7 <= availableWidth;
}

function buildEventLocationBoundaries(
  segments: EventLocationSegment[],
): Array<{ key: string; position: number }> {
  if (segments.length === 0) return [];

  const boundaries = segments.map((segment) => ({
    key: `start-${segment.key}`,
    position: segment.start,
  }));

  const last = segments[segments.length - 1];
  if (last.end > last.start) {
    boundaries.push({
      key: `end-${last.key}`,
      position: last.end,
    });
  }

  return boundaries;
}

function filterEventLocationBoundaries(
  boundaries: Array<{ key: string; position: number }>,
  visibleDomain: [number, number] | null,
): Array<{ key: string; position: number }> {
  if (!visibleDomain) return boundaries;
  return boundaries.filter((boundary) => boundary.position >= visibleDomain[0] && boundary.position <= visibleDomain[1]);
}

function computeChartHoursDomain(chartData: Array<{ hours?: number }>): [number, number] | null {
  const values = chartData.map((item) => item.hours).filter((value): value is number => Number.isFinite(value));
  if (values.length === 0) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [min - 1, max + 1];
  return [min, max];
}

function resolveZoomAnchor(hoveredHour: number | null, currentDomain: [number, number]): number {
  if (hoveredHour === null) {
    return (currentDomain[0] + currentDomain[1]) / 2;
  }
  if (hoveredHour < currentDomain[0]) {
    return currentDomain[0];
  }
  if (hoveredHour > currentDomain[1]) {
    return currentDomain[1];
  }
  return hoveredHour;
}

function zoomChartHoursDomain(
  currentDomain: [number, number],
  fullDomain: [number, number],
  anchor: number,
  deltaY: number,
): [number, number] {
  const fullMin = fullDomain[0];
  const fullMax = fullDomain[1];
  const fullRange = fullMax - fullMin;
  if (fullRange <= 0) return fullDomain;

  const zoomFactor = deltaY < 0 ? 0.8 : 1.25;
  const currentMin = currentDomain[0];
  const currentMax = currentDomain[1];
  const currentRange = currentMax - currentMin;
  const minRange = Math.max(fullRange / 200, 0.25);
  const nextRange = clampNumber(currentRange * zoomFactor, minRange, fullRange);
  const anchorRatio = (anchor - currentMin) / currentRange;
  const nextMin = anchor - nextRange * anchorRatio;

  return clampDomainToFullRange([nextMin, nextMin + nextRange], fullDomain);
}

function panChartHoursDomain(
  currentDomain: [number, number],
  fullDomain: [number, number],
  deltaX: number,
  width: number,
): [number, number] {
  if (width <= 0) return currentDomain;

  const range = currentDomain[1] - currentDomain[0];
  if (range <= 0) return currentDomain;

  const hoursDelta = (deltaX / width) * range;
  const nextMin = currentDomain[0] - hoursDelta;
  return clampDomainToFullRange([nextMin, nextMin + range], fullDomain);
}

function clampDomainToFullRange(domain: [number, number], fullDomain: [number, number]): [number, number] {
  const fullMin = fullDomain[0];
  const fullMax = fullDomain[1];
  const range = domain[1] - domain[0];

  if (domain[0] < fullMin) {
    return [fullMin, fullMin + range];
  }
  if (domain[1] > fullMax) {
    return [fullMax - range, fullMax];
  }
  return domain;
}

function clampNumber(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function formatAxisTick(value: unknown): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return String(Math.round(numeric));
}

function findFirstSeriesTimestampMs(series: TemperatureSeries[]): number | null {
  const values = series.flatMap((item) => item.points.map((point) => parseTimestampMs(point.timestamp)));
  const valid = values.filter((value): value is number => value !== null);
  if (!valid.length) return null;
  return Math.min(...valid);
}

function findLastSeriesHour(series: TemperatureSeries[]): number {
  const values = series.flatMap((item) => item.points.map((point) => point.hours));
  const valid = values.filter((value) => Number.isFinite(value));
  if (!valid.length) return 0;
  return Math.max(...valid);
}

function parseTimestampMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value).valueOf();
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

function formatEventLocationLabel(event: ConsignmentDetail["events"][number]): string | null {
  const iataCode = event.iataCode?.trim();
  if (iataCode) return iataCode;
  const country = event.country?.trim();
  if (country) return country;
  return null;
}

function formatEventLocationTitle(event: ConsignmentDetail["events"][number], label: string): string {
  const parts = [label];
  if (event.typeCode) parts.push(event.typeCode);
  if (event.description && event.description !== event.typeCode) parts.push(event.description);
  if (event.timestamp) parts.push(new Date(event.timestamp).toLocaleString("fr-FR"));
  return parts.join(" - ");
}

function renderHistogramSafeBand(
  bins: Array<{ range: string; count: number }>,
  safeRange: { min: number; max: number },
) {
  const band = resolveHistogramSafeBand(bins, safeRange);
  if (!band) return null;

  return (
    <ReferenceArea
      x1={band.start}
      x2={band.end}
      fill="#bbf7d0"
      fillOpacity={0.25}
      stroke="none"
      ifOverflow="extendDomain"
    />
  );
}

function resolveHistogramSafeBand(
  bins: Array<{ range: string; count: number }>,
  safeRange: { min: number; max: number },
): { start: string; end: string } | null {
  const matching = bins.filter((bin) => isHistogramBinWithinSafeRange(bin.range, safeRange));
  if (matching.length === 0) return null;
  return {
    start: matching[0].range,
    end: matching[matching.length - 1].range,
  };
}

function isHistogramBinWithinSafeRange(
  rangeLabel: string,
  safeRange: { min: number; max: number },
): boolean {
  const parsed = parseHistogramRange(rangeLabel);
  if (!parsed) return false;
  return parsed.end > safeRange.min && parsed.start < safeRange.max;
}

function parseHistogramRange(rangeLabel: string): { start: number; end: number } | null {
  const [startRaw, endRaw] = rangeLabel.split("-").map((value) => Number.parseFloat(value));
  if (!Number.isFinite(startRaw) || !Number.isFinite(endRaw)) return null;
  return {
    start: Math.min(startRaw, endRaw),
    end: Math.max(startRaw, endRaw),
  };
}

function formatSeriesLabel(
  item: TemperatureSeries,
  options?: {
    includePackageTypes?: boolean;
  },
): string {
  const deviceLabel = formatDeviceIdentifier(item.shipmentId, item.deviceId);
  const positionLabel = item.position?.trim() ? item.position : "—";
  const includePackageTypes = options?.includePackageTypes ?? true;
  if (!includePackageTypes) {
    return `${deviceLabel} (${positionLabel})`;
  }
  const packageTypes = getSeriesPackageTypes(item).filter((value) => value !== UNKNOWN_PACKAGE_TYPE);
  if (packageTypes.length > 0) {
    return `${deviceLabel} (${positionLabel}) - ${packageTypes.join(", ")}`;
  }
  return `${deviceLabel} (${positionLabel})`;
}

function buildSeriesColor(index: number): string {
  if (index < TEMPERATURE_COLOR_PALETTE.length) {
    return TEMPERATURE_COLOR_PALETTE[index];
  }

  const goldenAngle = 137.508;
  const hue = Math.round((index * goldenAngle) % 360);
  const lightness = 42 + ((index % 3) * 8);
  return `hsl(${hue} 72% ${lightness}%)`;
}
